import { uniqBy } from 'lodash';

import { CARGO_TYPES, CONTAINER_SIZE } from '@/lib/constants';
import { ShipmentJourneyModelSegment, ShipmentLocation } from '@/lib/models/shipment-journey/types';
import { EmissionsCalculatorSegmentRequestParams } from '@/types/api-request-types';
import { convertVehicleTypeToModeType } from '@/utils/format/mode';

import { ShipmentCargo, ShipmentContainer, ShipmentModel } from '../../types';

export const buildEmissionsSegments = (
  segments?: ShipmentJourneyModelSegment[],
): EmissionsCalculatorSegmentRequestParams[] => {
  return (segments || []).map((segment) => {
    const { vehicle, locations } = segment;
    const transformedLocations = locations
      .filter((l): l is ShipmentLocation & { portId: string } => l.portId !== undefined)
      .map((l) => ({
        id: l.portId,
        name: l.locationName,
      }));
    const vesselImo = vehicle.imoNumber ? `vesselImo ${vehicle.imoNumber}` : '';
    const vehicleId = vesselImo || '';
    const flightNumber = vehicle.flightNumber || '';
    // Only use uniq if more than 2 locations are provided
    const uniqLocations = transformedLocations.length > 2 ? uniqBy(transformedLocations, 'id') : transformedLocations;

    return {
      vehicle: {
        type: convertVehicleTypeToModeType(vehicle.type),
        ...(vehicleId ? { vehicleId } : {}),
        ...(flightNumber ? { flightNumber } : {}),
      },
      locations: uniqLocations,
    };
  });
};

export const normaliseContainerDetails = (container: ShipmentContainer): ShipmentContainer => {
  const { size } = container;
  let newSize = size;

  if (size) {
    if (size.includes('40')) newSize = CONTAINER_SIZE.FORTY;
    if (size.includes('20')) newSize = CONTAINER_SIZE.TWENTY;
    if (size.includes('53')) newSize = CONTAINER_SIZE.FIFTYTHREE;
  }
  return {
    ...container,
    size: newSize,
  };
};

export const normaliseCargoData = (cargo: ShipmentCargo[]): ShipmentCargo[] => {
  if (!cargo || !cargo.length) return [];

  return cargo.map((item) => {
    return {
      ...item,
      ...(item.container ? { container: normaliseContainerDetails(item.container) } : {}),
    };
  });
};

// Will try and extract cargo data from associatedContainers first, otherwise
// will use the cargo property. This is useful when the shipment is actually
// representing an MBOL collection instead of just a single container.
export const getCargoData = (shipment: ShipmentModel | null): ShipmentCargo[] => {
  if (!shipment) return [];

  if (shipment.associatedContainers?.length) {
    return shipment.associatedContainers.map((associatedContainer) => {
      return associatedContainer.cargo || { type: CARGO_TYPES.CONTAINER, quantity: 1 };
    });
  }
  return [shipment.cargo || { type: CARGO_TYPES.CONTAINER, quantity: 1 }];
};
